import api from "../../Utils/api";
import { toast } from "react-toastify";
import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { format, startOfWeek, addWeeks, subWeeks } from "date-fns";
import ReactApexChart from "react-apexcharts";

import EventDialog from "./EventDialog";
import UserElement from "../User/UserElement";
import CalendarGrid from "./CalendarGrid";
import LocationBadge from "./LocationBadge";
import WeekNavigator from "./WeekNavigator";
import ExportDropdown from "./ExportDropdown";
import ViewSelectorDropdown from "./ViewSelectorDropDown";
import OptionsDropdown from "./OptionDropdown";
import InsightsDropdown from "./InsideDropdown";
import CopyDropdown from "./CopyDropdown";
import LocationMultiSelect from "./LocationSelect";
import TotalCard from "./TotalCard";
import PublishShiftsDialog from "./PublishDialog";
import Spinner from "../Spinner";

const sortFields = [
  { label: "First Name", value: "preferredName" },
  { label: "Last Name", value: "lastName" },
  // { label: "Total Hours", value: "totalHours" },
  // Add more as needed
];

export default function CalendarPage() {
  const [date, setDate] = useState(new Date());
  const [users, setUsers] = useState([]);
  const [period, setPeriod] = useState(() => {
    return parseInt(localStorage.getItem("period") ?? "7", 10);
  });

  const [modify, setModify] = useState(false);
  const [toggles, setToggles] = useState(() => {
    const stored = localStorage.getItem("toggles");
    return stored ? JSON.parse(stored) : [];
  });
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("viewMode") ?? "location"
  );
  // "location" or "member"
  const [userID, setUserID] = useState("");
  const [locations, setLocations] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [locationID, setLocationID] = useState("");
  const [modifyData, setModifyData] = useState(null);
  const [showInsight, setShowInsight] = useState(false);
  const [allSchedules, setAllSchedules] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [leavesOrUnavailables, setLeavesOrUnavailables] = useState([]);

  const [categories, setCategories] = useState([]);
  const [shiftData, setShiftData] = useState([]);
  const [emptyData, setEmptyData] = useState([]);

  const [dayEvents, setDayEvents] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("preferredName");
  const [sortOrder, setSortOrder] = useState("asc");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const options = {
    chart: {
      type: "bar",
      height: 350,
      stacked: true,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 10,
        borderRadiusApplication: "end",
        borderRadiusWhenStacked: "last",
        columnWidth: "50%", // Ensures uniform bar width
        dataLabels: {
          total: {
            enabled: true,
            style: {
              fontSize: "13px",
              fontWeight: 900,
              color: "gray",
            },
            formatter: function (val) {
              return val.toFixed(2); // Always shows two decimals
            },
          },
        },
      },
    },
    xaxis: {
      categories: categories, // your array of dates
      labels: { show: false },
      axisTicks: { show: false },
      axisBorder: { show: false },
      tickPlacement: "between", // Key for cell alignment[3]
    },
    yaxis: { show: false },
    grid: { show: false },
    legend: { show: false },
    tooltip: { enabled: true },
    fill: { opacity: 1 },
    colors: ["#3396FF", "#38D996"],
  };

  const series = [
    {
      name: "Shift",
      data: shiftData,
    },
    {
      name: "Empty",
      data: emptyData,
    },
  ];

  useEffect(() => {
    getUsers();
    getLocations();
    getDayEvents();
  }, []);

  useEffect(() => {
    localStorage.setItem("toggles", JSON.stringify(toggles));
  }, [toggles]);

  useEffect(() => {
    getSchedules();
  }, [selectedDate, period]);

  useEffect(() => {
    const filteredSchedules = schedules.filter((schedule) =>
      filteredLocations.some(
        (targetName) =>
          schedule.locationID &&
          schedule.locationID.name &&
          schedule.locationID.name.trim() === targetName.trim()
      )
    );

    const chartData = getScheduleDataByPeriod(
      filteredSchedules,
      selectedDate,
      period
    );

    // Extract categories (dates)
    setCategories(chartData.map((item) => item.date));
    setShiftData(chartData.map((item) => item.shift));
    setEmptyData(chartData.map((item) => item.empty));

    if (users.length > 0) {
      let usersRef = users;
      setUsers([]);
      setUsers(usersRef);
    }
  }, [schedules, selectedDate, filteredLocations]);

  useEffect(() => {
    if (modify && modifyData != null) {
      setIsDialogOpen(true);
    }
  }, [modify]);

  const openEventDialog = (date, locationID, userID) => {
    setIsDialogOpen(true);
    setLocationID(locationID);
    setUserID(userID);
    setDate(date);
  };

  const openModifyDialog = (entry) => {
    setModify(true);

    if (entry["userID"] == undefined) {
      const userID = {
        _id: entry.type,
      };
      entry["userID"] = userID;
    }

    setModifyData(entry);
  };

  const closeEventDialog = () => {
    setIsDialogOpen(false);
    setModify(false);
  };

  const toggleById = (id) => {
    setToggles((prev) => {
      if (prev.includes(id)) {
        // remove it
        return prev.filter((item) => item !== id);
      } else {
        // add it
        return [...prev, id];
      }
    });
  };

  const getUsers = async () => {
    try {
      setIsLoading(true);
      const response = await api.get("/user");
      setIsLoading(false);
      setUsers(response.data.users.filter((u) => !u.archive));
    } catch (error) {
      console.error(error);
    }
  };

  const getLocations = async () => {
    try {
      setIsLoading(true);
      const response = await api.get("/location");
      setIsLoading(false);

      setLocations(response.data.locations.filter((loc) => !loc.archive));
    } catch (error) {
      console.error(error);
    }
  };

  const getDayEvents = async () => {
    const start = new Date(selectedDate);
    start.setUTCHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + period);
    end.setUTCHours(23, 59, 59, 999);
    setIsLoading(true);
    const res = await api.post("/schedule/day_events_list", {
      startTime: start.toISOString(),
      endTime: end.toISOString(),
    });
    setIsLoading(false);
    setDayEvents(res.data.events);
  };

  const getSchedules = async () => {
    try {
      const start = new Date(selectedDate); // selectedDate should be a Date object
      start.setUTCHours(0, 0, 0, 0); // Set to 00:00:00.000 UTC

      const end = new Date(start); // clone the start date
      end.setUTCDate(end.getUTCDate() + period); // add (period - 1) days to include start date
      end.setUTCHours(23, 59, 59, 999); // Set to 23:59:59.999 UTC

      setIsLoading(true);

      const response = await api.post(`/schedule/list`, {
        startTime: start.toISOString(), // full ISO with time
        endTime: end.toISOString(), // full ISO with time
        admin: true,
      });
      setIsLoading(false);
      setSchedules(response.data.schedules);

      let leaves = await response.data.leaves.map((item) =>
        splitLeaveOrUnavialableByDay(item)
      );

      let unavailables = await response.data.unavailables.map((item) =>
        splitLeaveOrUnavialableByDay(item)
      );

      setLeavesOrUnavailables([...leaves.flat(), ...unavailables.flat()]);

      // ! .sort() REFRESH => PAGE
      setAllSchedules(
        [
          ...response.data.schedules.flat(),
          ...leaves.flat(),
          ...unavailables.flat(),
        ].sort()
      );

      if (users.length > 0) {
        let temp_users = [...users];
        setUsers([]);
        setUsers(temp_users);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const splitLeaveOrUnavialableByDay = (schedule) => {
    if (schedule.type !== "unavailable") {
      let leaveType = schedule.type;
      schedule.type = "leave";
      schedule.leaveType = leaveType;
    }

    const start = new Date(schedule.startTime);
    const end = new Date(schedule.endTime);

    // Helper to format date as YYYY-MM-DD
    const formatDate = (d) => d.toISOString().slice(0, 10);

    const segments = [];
    let current = new Date(start);

    let dayIndex = 1;
    while (current < end) {
      // Calculate day's end (either at midnight or the leave's end)
      const nextDay = new Date(current);
      nextDay.setUTCHours(24, 0, 0, 0); // midnight of next day

      // The segment ends at either the next day's midnight or the leave's end time
      const segmentEnd = nextDay < end ? nextDay : end;

      segments.push({
        name: `Day ${dayIndex} (${formatDate(current)})`,
        startTime: current.toISOString(),
        type: schedule.type,
        userID: schedule.userID,
        endTime: segmentEnd.toISOString(),
      });

      // Move to the next day
      current = segmentEnd;
      dayIndex += 1;
    }

    return segments;
  };

  const getScheduleSummary = (userID, filteredLocations) => {
    // Filter schedules for the given userID

    const filteredSchedules = schedules.filter((schedule) =>
      filteredLocations.some(
        (targetName) =>
          schedule.locationID &&
          schedule.locationID.name &&
          schedule.locationID.name.trim() === targetName.trim()
      )
    );

    const userSchedules = filteredSchedules.filter(
      (s) => s.userID?._id === userID || s.userID?.id === userID
    );

    let totalMilliseconds = 0;

    userSchedules.forEach((shift) => {
      const start = new Date(shift.startTime);
      const end = new Date(shift.endTime);

      let shiftDuration = end - start; // total shift duration in ms

      // Calculate break duration if break times exist
      if (shift.breakStartTime && shift.breakEndTime) {
        const breakStart = new Date(shift.breakStartTime);
        const breakEnd = new Date(shift.breakEndTime);
        const breakDuration = breakEnd - breakStart;

        if (breakDuration > 0 && breakDuration < shiftDuration) {
          shiftDuration -= breakDuration;
        }
      }

      if (shiftDuration > 0) {
        totalMilliseconds += shiftDuration;
      }
    });

    const totalHours = totalMilliseconds / (1000 * 60 * 60);
    const roundedHours = Math.round(totalHours);

    const shiftCount = userSchedules.length;

    return `${roundedHours}hrs / ${shiftCount} shift${
      shiftCount !== 1 ? "s" : ""
    }`;
  };

  const getExportData = () => {
    // Helper to format date and time
    const formatDate = (isoString) => {
      if (!isoString) return "";
      const date = new Date(isoString);
      return date.toLocaleDateString();
    };
    const formatTime = (timeString) => {
      if (!timeString) return "-";
      const date = new Date(timeString);

      // Format the time using Intl.DateTimeFormat
      return new Intl.DateTimeFormat([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false, // use false for 24-hour format, true for AM/PM
        timeZone: "UTC", // optional: remove if you want local time
      }).format(date);
    };

    // Helper to format total hours as "Xhr Ymin"
    const formatTotalHours = (ms) => {
      if (!ms || ms <= 0) return "";
      const totalMinutes = Math.round(ms / (1000 * 60));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      let str = "";
      if (hours > 0) str += `${hours}hr `;
      if (minutes > 0) str += `${minutes}min`;
      return str.trim();
    };

    // Sort schedules by startTime ascending
    const sortedSchedules = [...schedules].sort(
      (a, b) => new Date(a.startTime) - new Date(b.startTime)
    );

    // Map to export format
    return sortedSchedules.map((schedule, idx) => {
      // Calculate total hours in ms
      const start = schedule.startTime ? new Date(schedule.startTime) : null;
      const end = schedule.endTime ? new Date(schedule.endTime) : null;
      const breakStart = schedule.breakStartTime
        ? new Date(schedule.breakStartTime)
        : null;
      const breakEnd = schedule.breakEndTime
        ? new Date(schedule.breakEndTime)
        : null;

      let totalMs = 0;
      if (start && end) {
        totalMs = end - start;
        if (breakStart && breakEnd) {
          totalMs -= breakEnd - breakStart;
        }
      }

      return {
        Number: idx + 1,
        User: schedule.userID?.preferredName ?? "",
        "Location Name": schedule.locationID?.name ?? "",
        "Location Area": schedule.locationID?.area ?? "",
        Date: formatDate(schedule.startTime),
        "Start Time": formatTime(schedule.startTime),
        "End Time": formatTime(schedule.endTime),
        "Break Start Time": formatTime(schedule.breakStartTime),
        "Break End Time": formatTime(schedule.breakEndTime),
        "Total Hours": formatTotalHours(totalMs),
      };
    });
  };

  const filteredAndSortedUsers = () => {
    let filtered = users.filter((user) =>
      user.preferredName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    filtered.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      if (aValue === undefined || bValue === undefined) return 0;
      if (typeof aValue === "string") {
        return sortOrder === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      // For numbers
      return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
    });
    return filtered;
  };

  return (
    <div className="flex flex-wrap gap-6">
      {isLoading && <Spinner />}

      <div className="flex w-full border-b border-gray-200 pb-4 justify-between items-center">
        {/* Left: View mode select */}

        <div className="flex justify-center items-center">
          <div className="flex items-center">
            <LocationMultiSelect
              setFilteredLocations={setFilteredLocations}
              locations={locations.map((item) => item.name)}
            />
            <WeekNavigator
              selectedDate={selectedDate}
              period={period}
              setSelectedDate={setSelectedDate}
            />
            <ViewSelectorDropdown
              period={period}
              viewMode={viewMode}
              setPeriod={setPeriod}
              setViewMode={setViewMode}
            />
          </div>
        </div>

        <div className="flex items-center gap-1 pr-8">
          <button
            type="button"
            className="py-2 px-3 rounded bg-gray-200 md:hidden  hover:bg-gray-300 transition-colors"
            title="Reload"
            onClick={() => window.location.reload()}
          >
            <i
              className="fa fa-rotate-right text-gray-700 text-sm"
              aria-hidden="true"
            ></i>
          </button>

          <CopyDropdown
            selectedDate={selectedDate}
            period={period}
            schedules={schedules}
            getSchedules={getSchedules}
          />
          <InsightsDropdown
            showInsight={showInsight}
            setShowInsight={setShowInsight}
          />
          <ExportDropdown data={getExportData()} />
          <OptionsDropdown
            locations={locations
              .filter((loc) => filteredLocations.includes(loc.name.trim()))
              .map((loc) => ({
                _id: loc._id,
                name: loc.name.trim(),
              }))}
            selectedDate={selectedDate}
            period={period}
            getSchedules={getSchedules}
            shiftCount={
              schedules.filter((schedule) =>
                filteredLocations.some(
                  (targetName) =>
                    schedule.locationID &&
                    schedule.locationID.name &&
                    schedule.locationID.name.trim() === targetName.trim()
                )
              ).length
            }
          />
          <PublishShiftsDialog
            selectedDate={selectedDate}
            period={period}
            getSchedules={getSchedules}
            schedules={schedules.filter((schedule) =>
              filteredLocations.some(
                (targetName) =>
                  schedule.locationID &&
                  schedule.locationID.name &&
                  schedule.locationID.name.trim() === targetName.trim()
              )
            )}
          />
        </div>
      </div>
      {/* You can conditionally render your calendar grid or other components here based on viewMode */}
      {viewMode === "location" ? (
        <div className="h-[calc(100vh-180px)] overflow-y-auto w-full  flex items-start overflow-hidden ">
          <div className="w-[12%] min-w-[230px] border-r border-gray-200 h-full  overflow-y-auto mr-2">
            <div className="flex inline mb-2 bg-white sticky top-0">
              <input
                className="border w-32 mr-2 rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-gray-300"
                value={searchTerm}
                placeholder="Search"
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              {/* Sort Dropdown Button */}
              <div className="relative">
                <button
                  className="border rounded px-2 py-1 bg-white flex items-center gap-1"
                  onClick={() => setDropdownOpen((open) => !open)}
                >
                  <span className="hidden sm:inline">Sort</span>
                </button>
                {dropdownOpen && (
                  <div className="absolute left-0 mt-2 w-48 bg-white border rounded-lg shadow-lg z-20">
                    {sortFields.map((field) => (
                      <div
                        key={field.value}
                        className={`flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                          sortField === field.value
                            ? "font-bold text-gray-700"
                            : ""
                        }`}
                        onClick={() => {
                          setSortField(field.value);
                          setDropdownOpen(false);
                        }}
                      >
                        {field.label}
                        {sortField === field.value && (
                          <span
                            className="ml-auto cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSortOrder((order) =>
                                order === "asc" ? "desc" : "asc"
                              );
                            }}
                          >
                            {sortOrder === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {showInsight && (
              <TotalCard
                totalTime={getTotalShiftTime(
                  schedules.filter((schedule) =>
                    filteredLocations.some(
                      (targetName) =>
                        schedule.locationID &&
                        schedule.locationID.name &&
                        schedule.locationID.name.trim() === targetName.trim()
                    )
                  )
                )}
                viewMode={viewMode}
              />
            )}

            {filteredAndSortedUsers().map((user, index) => (
              <UserElement
                sheetNumber={getScheduleSummary(user._id, filteredLocations)}
                key={`index` + index}
                user={user}
              />
            ))}
          </div>
          <div className={`w-[86%] h-full overflow-y-auto `}>
            <div className="sticky bg-white top-0 border-b border-gray-200 z-10">
              <div className="flex w-full mb-4 pl-7  sticky top-0 bg-white z-10">
                <CalendarGrid
                  schedules={schedules}
                  startDate={selectedDate}
                  dayEvents={dayEvents}
                  getDayEvents={getDayEvents}
                  period={period}
                  displayDay={true}
                />
              </div>

              {showInsight && (
                <>
                  <hr className="w-full mb-4" />
                  <div className="flex w-full pl-7  bg-white z-10">
                    <CalendarGrid
                      schedules={schedules.filter((schedule) =>
                        filteredLocations.some(
                          (targetName) =>
                            schedule.locationID &&
                            schedule.locationID.name &&
                            schedule.locationID.name.trim() ===
                              targetName.trim()
                        )
                      )}
                      startDate={selectedDate}
                      period={period}
                      showInsight={true}
                    />
                  </div>
                  <div className="bg-white/70 ml-6">
                    <ReactApexChart
                      options={options}
                      series={series}
                      type="bar"
                      height={200}
                      width="100%"
                    />
                  </div>
                </>
              )}
            </div>

            <LocationBadge
              toggle={toggles.find((t) => t == "timeoff")}
              toggleById={toggleById}
              area="Time Off"
              _id="timeoff"
            />
            <div
              className={`flex w-full items-stretch mb-2 transition-all duration-500 ease-in-out pl-7 ${
                toggles.find((t) => t == "timeoff")
                  ? "max-h-[500px] opacity-100 translate-y-0"
                  : "max-h-0 opacity-0 translate-y-4 hidden"
              }`}
            >
              <CalendarGrid
                getSchedules={getSchedules}
                openModifyDialog={openModifyDialog}
                schedules={leavesOrUnavailables}
                openDialog={openEventDialog}
                startDate={selectedDate}
                period={period}
                timeoff={true}
                location={false} // ! MEANS => BY LOCATION
              />
            </div>
            {locations
              .filter((location) =>
                filteredLocations.some(
                  (targetName) => location.name.trim() === targetName.trim()
                )
              )
              .map((item, index) => (
                <>
                  <LocationBadge
                    toggle={toggles.find((t) => t == item._id)}
                    toggleById={toggleById}
                    {...item}
                  />
                  <div
                    className={`flex w-full items-stretch mb-2 transition-all duration-500 ease-in-out pl-7 ${
                      toggles.find((t) => t == item._id)
                        ? "max-h-[500px] opacity-100 translate-y-0"
                        : "max-h-0 opacity-0 translate-y-4 hidden"
                    }`}
                  >
                    <CalendarGrid
                      getSchedules={getSchedules}
                      openModifyDialog={openModifyDialog}
                      schedules={schedules.filter(
                        (sc) => sc.locationID?._id == item._id
                      )}
                      openDialog={openEventDialog}
                      startDate={selectedDate}
                      locationID={item._id}
                      period={period}
                      location={false} // ! MEANS => BY LOCATION, if TRUE => I have to give LocationID
                    />
                  </div>
                </>
              ))}
          </div>
        </div>
      ) : (
        <div className="h-[calc(100vh-180px)] overflow-y-auto w-full  flex items-start  ">
          <div className="w-full border-r border-gray-200 mr-2">
            {/* <div className="flex sticky top-0">
              <div className="w-[200px] border-b border-gray-100"></div>
              <div className="w-[calc(100%-200px)]  bg-white">
                <div className="sticky top-0 w-full flex inline bg-white z-10">
                  <CalendarGrid
                    schedules={schedules}
                    startDate={selectedDate}
                    period={period}
                    displayDay={true}
                  />
                </div>
              </div>
            </div> */}
            <div className="flex w-full inline sticky top-0 z-10 bg-white pb-2">
              <div className="flex w-[200px]">
                <input
                  className="border w-32 mr-2 rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-gray-300"
                  value={searchTerm}
                  placeholder="Search"
                  onChange={(e) => setSearchTerm(e.target.value)}
                />

                {/* Sort Dropdown Button */}
                <div className="relative">
                  <button
                    className="border rounded px-2 py-1 bg-white flex items-center gap-1"
                    onClick={() => setDropdownOpen((open) => !open)}
                  >
                    <span className="hidden sm:inline">Sort</span>
                  </button>
                  {dropdownOpen && (
                    <div className="absolute left-0 mt-2 w-48 bg-white border rounded-lg shadow-lg z-10">
                      {sortFields.map((field) => (
                        <div
                          key={field.value}
                          className={`flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                            sortField === field.value
                              ? "font-bold text-gray-700"
                              : ""
                          }`}
                          onClick={() => {
                            setSortField(field.value);
                            setDropdownOpen(false);
                          }}
                        >
                          {field.label}
                          {sortField === field.value && (
                            <span
                              className="ml-auto cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSortOrder((order) =>
                                  order === "asc" ? "desc" : "asc"
                                );
                              }}
                            >
                              {sortOrder === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex w-[calc(100%-200px)]">
                <CalendarGrid
                  schedules={schedules}
                  startDate={selectedDate}
                  period={period}
                  dayEvents={dayEvents}
                  getDayEvents={getDayEvents}
                  displayDay={true}
                />
              </div>
            </div>
            {showInsight && (
              <div className="flex justify-between w-full">
                <TotalCard
                  totalTime={getTotalShiftTime(
                    schedules.filter((schedule) =>
                      filteredLocations.some(
                        (targetName) =>
                          schedule.locationID &&
                          schedule.locationID.name &&
                          schedule.locationID.name.trim() === targetName.trim()
                      )
                    )
                  )}
                  viewMode={viewMode}
                />
                <div className="w-[calc(100%-200px)]">
                  <div className="flex w-full  bg-white z-10">
                    <CalendarGrid
                      schedules={schedules.filter((schedule) =>
                        filteredLocations.some(
                          (targetName) =>
                            schedule.locationID &&
                            schedule.locationID.name &&
                            schedule.locationID.name.trim() ===
                              targetName.trim()
                        )
                      )}
                      startDate={selectedDate}
                      period={period}
                      showInsight={true}
                    />
                  </div>
                  <div className="bg-white/70 ">
                    <ReactApexChart
                      options={options}
                      series={series}
                      type="bar"
                      height={200}
                      width="100%"
                    />
                  </div>
                  <hr className="w-full mb-4" />
                </div>
              </div>
            )}

            {filteredAndSortedUsers().map((user) => (
              <div className="flex" key={user._id}>
                <div className="w-[200px] border-b border-gray-100">
                  <UserElement
                    sheetNumber={getScheduleSummary(
                      user._id,
                      filteredLocations
                    )}
                    user={user}
                  />
                </div>
                <div className="w-[calc(100%-200px)] flex">
                  <CalendarGrid
                    getSchedules={getSchedules}
                    openModifyDialog={openModifyDialog}
                    schedules={allSchedules
                      .filter((schedule) =>
                        filteredLocations.some(
                          (targetName) =>
                            schedule.locationID &&
                            schedule.locationID.name &&
                            schedule.locationID.name.trim() ===
                              targetName.trim()
                        )
                      )
                      .filter((sc) => sc.userID?.id === user._id)}
                    openDialog={openEventDialog}
                    startDate={selectedDate}
                    period={period}
                    location={true} // ! Only in Locations
                    userID={user._id} // ! Only in Locations
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <EventDialog
        modify={modify}
        modifyData={modifyData}
        getSchedules={getSchedules}
        selectedUserID={userID}
        selectedLocationID={locationID}
        locations={locations.filter((loc) => !loc.archive)}
        users={users}
        date={date}
        isOpen={isDialogOpen}
        onClose={closeEventDialog}
      />
    </div>
  );
}

function getScheduleDataByPeriod(schedules, startDate, period) {
  // Helper to format date as YYYY-MM-DD
  const pad = (n) => n.toString().padStart(2, "0");

  const getLocalYMD = (date) => {
    const yyyy = date.getFullYear();
    const mm = pad(date.getMonth() + 1); // Months are 0-based
    const dd = pad(date.getDate());
    return `${yyyy}-${mm}-${dd}`;
  };

  // Generate all dates in the period
  const dates = [];
  const start = startOfWeek(new Date(startDate), { weekStartsOn: 1 });
  for (let i = 0; i < period; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    dates.push(getLocalYMD(d));
  }

  // Initialize result map
  const resultMap = {};
  dates.forEach((date) => {
    resultMap[date] = { date, shift: 0, open: 0, empty: 0 };
  });

  // Process each schedule
  schedules.forEach((schedule) => {
    // Get schedule start and end as Date objects
    const schedStart = new Date(schedule.startTime);
    schedStart.setFullYear(schedStart.getUTCFullYear());
    schedStart.setMonth(schedStart.getUTCMonth());
    schedStart.setDate(schedStart.getUTCDate());
    schedStart.setHours(schedStart.getUTCHours());
    schedStart.setMinutes(schedStart.getUTCMinutes());
    schedStart.setSeconds(schedStart.getUTCSeconds());

    const schedEnd = new Date(schedule.endTime);
    schedEnd.setFullYear(schedEnd.getUTCFullYear());
    schedEnd.setMonth(schedEnd.getUTCMonth());
    schedEnd.setDate(schedEnd.getUTCDate());
    schedEnd.setHours(schedEnd.getUTCHours());
    schedEnd.setMinutes(schedEnd.getUTCMinutes());
    schedEnd.setSeconds(schedEnd.getUTCSeconds());

    // Only consider schedules that overlap the period
    for (let i = 0; i < period; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      const dayStr = getLocalYMD(day);

      // Day start/end boundaries
      // const dayStart = new Date(dayStr + "T00:00:00.000Z");
      // const dayEnd = new Date(dayStr + "T23:59:59.999Z");

      const dayStart = new Date(dayStr);
      dayStart.setFullYear(dayStart.getUTCFullYear());
      dayStart.setMonth(dayStart.getUTCMonth());
      dayStart.setDate(dayStart.getUTCDate());
      dayStart.setHours(0);
      dayStart.setMinutes(0);
      dayStart.setSeconds(0);

      // End of day (23:59:59.999)
      const dayEnd = new Date(dayStr);
      dayEnd.setFullYear(dayEnd.getUTCFullYear());
      dayEnd.setMonth(dayEnd.getUTCMonth());
      dayEnd.setDate(dayEnd.getUTCDate());
      dayEnd.setHours(23);
      dayEnd.setMinutes(59);
      dayEnd.setSeconds(59);

      // Find overlap between schedule and this day
      const overlapStart = schedStart > dayStart ? schedStart : dayStart;
      const overlapEnd = schedEnd < dayEnd ? schedEnd : dayEnd;

      if (overlapStart < overlapEnd) {
        // Subtract break time if break is within this day
        let breakMs = 0;
        if (schedule.breakStartTime && schedule.breakEndTime) {
          const breakStart = new Date(schedule.breakStartTime);
          breakStart.setFullYear(breakStart.getUTCFullYear());
          breakStart.setMonth(breakStart.getUTCMonth());
          breakStart.setDate(breakStart.getUTCDate());
          breakStart.setHours(breakStart.getUTCHours());
          breakStart.setMinutes(breakStart.getUTCMinutes());
          breakStart.setSeconds(breakStart.getUTCSeconds());

          const breakEnd = new Date(schedule.breakEndTime);
          breakEnd.setFullYear(breakEnd.getUTCFullYear());
          breakEnd.setMonth(breakEnd.getUTCMonth());
          breakEnd.setDate(breakEnd.getUTCDate());
          breakEnd.setHours(breakEnd.getUTCHours());
          breakEnd.setMinutes(breakEnd.getUTCMinutes());
          breakEnd.setSeconds(breakEnd.getUTCSeconds());

          // Only count break time that overlaps this day
          const breakOverlapStart =
            breakStart > dayStart ? breakStart : dayStart;
          const breakOverlapEnd = breakEnd < dayEnd ? breakEnd : dayEnd;
          if (breakOverlapStart < breakOverlapEnd) {
            breakMs = breakOverlapEnd - breakOverlapStart;
          }
        }
        // Calculate hours for this day
        const hours = (overlapEnd - overlapStart - breakMs) / (1000 * 60 * 60);

        // Determine type
        let type = "empty";
        if (schedule.type === "open" || schedule.type == "open_claim")
          type = "empty";
        else if (
          schedule.type === "shift" ||
          schedule.type == "shift_offer" ||
          schedule.type === "shift_swap"
        )
          type = "shift";

        resultMap[dayStr][type] += hours;
      }
    }
  });

  // Combine open and empty into empty, set open to 0
  return dates.map((date) => {
    const originalOpen = resultMap[date].open;
    let empty = Math.round((resultMap[date].empty + originalOpen) * 100) / 100;

    // Fix: Reset empty to 0 if no open schedules and empty ≈1 due to precision
    if (originalOpen === 0 && Math.abs(empty - 1) < 0.01) {
      empty = 0;
    }

    return {
      date,
      shift: Math.round(resultMap[date].shift * 100) / 100,
      open: 0,
      empty: empty,
    };
  });
}

function getTotalShiftTime(schedules) {
  const resultMap = {}; // { date: totalShiftHours }

  schedules.forEach((schedule) => {
    // Skip non-shift schedules
    if (["leave", "unavailable"].includes(schedule.type)) return;

    const schedStart = new Date(schedule.startTime);
    const schedEnd = new Date(schedule.endTime);

    // Get all dates covered by this schedule
    const currentDate = new Date(schedStart);
    currentDate.setUTCHours(0, 0, 0, 0); // Start at midnight

    while (currentDate <= schedEnd) {
      const dateStr = currentDate.toISOString().split("T")[0];

      // Day boundaries
      const dayStart = new Date(dateStr + "T00:00:00.000Z");
      const dayEnd = new Date(dateStr + "T23:59:59.999Z");

      // Calculate overlap
      const overlapStart = schedStart > dayStart ? schedStart : dayStart;
      const overlapEnd = schedEnd < dayEnd ? schedEnd : dayEnd;

      if (overlapStart < overlapEnd) {
        // Calculate hours for this day
        let hours = (overlapEnd - overlapStart) / (1000 * 60 * 60);

        // Subtract break time if exists
        if (schedule.breakStartTime && schedule.breakEndTime) {
          const breakStart = new Date(schedule.breakStartTime);
          const breakEnd = new Date(schedule.breakEndTime);

          // Break overlap with this day
          const breakOverlapStart =
            breakStart > dayStart ? breakStart : dayStart;
          const breakOverlapEnd = breakEnd < dayEnd ? breakEnd : dayEnd;

          if (breakOverlapStart < breakOverlapEnd) {
            hours -= (breakOverlapEnd - breakOverlapStart) / (1000 * 60 * 60);
          }
        }

        // Add to result map
        resultMap[dateStr] = (resultMap[dateStr] || 0) + hours;
      }

      // Move to next day

      currentDate.setDate(currentDate.getDate() + 1);
    }
  });

  // Sum all shift hours and round to 2 decimals
  const totalShift = Object.values(resultMap).reduce(
    (sum, hours) => sum + hours,
    0
  );

  return Math.round(totalShift * 100) / 100;
}
